<script setup>
import { ref } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import { useStore } from 'vuex'
import Playlistservice from '@/services/Playlistservice'
import ModalBox from './ModalBox.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import Alert from '@/components/Atoms/Alert.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import { track, EVENTS } from '@/lib/tracking'

const store = useStore()
const { notify } = useNotification()

const props = defineProps({
	playlist: {
		type: Array,
		default: () => [],
	},
})

const emit = defineEmits(['playlist-created'])

const isVisible = ref(false)
const playlistName = ref('')
const error = ref(null)

const toggleModal = () => {
	isVisible.value = !isVisible.value
}

const closeModal = () => {
	playlistName.value = ''
	isVisible.value = false
}

const onReset = () => {
	error.value = null
}

const onConfirm = async () => {
	try {
		store.dispatch('Ui/setLoadingStatus', true)

		const exists = props.playlist?.length > 0
			? props.playlist.filter((s) => s.name === playlistName.value)
			: []

		if (exists.length > 0) {
			notify({ group: 'error', text: `A playlist named "${playlistName.value}" already exists.` })
		} else {
			const newPlaylist = await Playlistservice.createPlaylist(
				playlistName.value,
				[]
			)
			track(EVENTS.PLAYLIST_CREATED, { playlistId: newPlaylist?._id || newPlaylist?.id })
			notify({ group: 'success', text: 'New Playlist Created' })
			closeModal()
			await store.dispatch('Playlist/getPlaylist', true)
			emit('playlist-created', newPlaylist)
		}

		store.dispatch('Ui/setLoadingStatus', false)
	} catch (err) {
		error.value = err.message
		store.dispatch('Ui/setLoadingStatus', false)
	}
}

defineExpose({ toggleModal })
</script>

<template>
	<ModalBox v-model="isVisible" size="sm" mobile-placement="bottom" title="Create new playlist" wrapper-classes="md:max-h-[90vh] overflow-y-auto" header-class="!border-b !border-surface-3 !py-3 !mb-3"
		@close="onReset">
		<div class="flex flex-col w-full px-6 pb-6">
			<Alert v-if="error" type="error" class="flex items-start px-2">
				<div v-if="error === 'Please contact the sales team to get more playlists.'" class="-mt-1">
					<p class="text-sm font-medium mb-0">You don’t have enough quota</p>
					<p class="text-tiny font-medium mb-0">
						Please contact
						<FormButton type="link" size="sm" class="underline text-white" onclick="Intercom('show')">sales</FormButton>
						team to get more playlists
					</p>
				</div>
				<div class="-mt-1" v-else>
					<p class="text-s-m text-500 mb-0">{{ error }}</p>
				</div>
			</Alert>

			<FormInput label="Playlist name" v-model="playlistName" placeholder="e.g., Movie playlist" />

			<div class="text-center mt-4 lg:border-t lg:border-surface-3 pt-4 flex flex-col gap-2 lg:flex-row lg:justify-end">
				<FormButton type="secondary" size="md" class="justify-center order-2 lg:order-1" @click="closeModal">
					Cancel
				</FormButton>
				<FormButton :disabled="playlistName.length === 0" @click="onConfirm" type="main" size="md"
					class="justify-center order-1 lg:order-2">
					Create playlist
				</FormButton>
			</div>
		</div>
	</ModalBox>
</template>

<style scoped></style>
